/**
 * 3D building extrusions with real surveyed heights.
 *
 * Heights come from Denver's own building outlines, not OSM guesses or
 * levels×3 estimates. The build script converts BLDG_HEIGH (feet) to metres,
 * so `properties.h` is already metres above ground.
 *
 * Footprint fidelity: polygons pass through untouched apart from rounding to
 * ~1m. Interior rings (courtyards, light wells) survive — SolidPolygonLayer
 * treats rings after the first as holes, the same convention GeoJSON uses, so
 * no conversion is needed. MultiPolygons split into one row per member at
 * load, because the layer takes one ring set per row.
 *
 * Shading is height-driven greyscale: taller reads lighter. The accent is
 * never spent here — buildings are scenery, data owns the colour.
 *
 * An underground view lived here too and has been removed. OpenStreetMap tags
 * exactly 7 underground car parks in all of downtown Denver, out of 1,803
 * parking areas, and depths were inferred from the `layer` tag — a
 * drawing-order hint, not a measurement. It looked like knowledge and was not.
 *
 * The procedural window shader that used to live here is gone. It never
 * compiled on this hardware, and because a failed compile takes the whole
 * layer with it, buildings vanished above zoom 15.5 — the exact zoom where
 * the shader switched on.
 */

import { SolidPolygonLayer } from "@deck.gl/layers";

import { toPolygons } from "../lib/geo.js";
import { palette, themeName } from "../theme/palette.js";
import { footprints, inRing, outerRing } from "./footprints.js";
import { pmTileLayer, tileFeatures } from "./tiles.js";
import { DEPTH_TESTED } from "./depth.js";

export { toPolygons };

const DATA_URL = "./data/buildings.geojson";

/** Tallest thing downtown is ~218m; normalise shading against that. */
const REFERENCE_HEIGHT_M = 220;

export async function loadBuildings() {
  try {
    const response = await fetch(DATA_URL);
    if (!response.ok) throw new Error(String(response.status));
    const body = await response.json();
    return { features: body.features ?? [], metadata: body.metadata ?? null };
  } catch (error) {
    console.warn("buildings unavailable:", error.message);
    return { features: [], metadata: null };
  }
}

/** Greyscale by height. Short buildings recede, towers catch the light. */
function shade(height) {
  const t = Math.min(height / REFERENCE_HEIGHT_M, 1);
  // Interpolated between the palette's two ends rather than a fixed grey ramp,
  // so a light theme gets a light city without inverting the height cue: low
  // still reads as low, whichever direction the ramp runs.
  const [lr, lg, lb] = palette().buildingLow;
  const [hr, hg, hb] = palette().buildingHigh;
  return [
    Math.round(lr + (hr - lr) * t),
    Math.round(lg + (hg - lg) * t),
    Math.round(lb + (hb - lb) * t),
  ];
}

/**
 * A cold cast for buildings nothing is known about.
 *
 * Denver's outlines give every footprint a surveyed height and a coarse class,
 * and that is all. Whether a building has a name, a use, opening hours or an
 * operator depends on someone having tagged it in OSM, and for most of
 * downtown nobody has. Rendering those identically to a fully-described
 * building makes absence look like presence — you cannot tell "an office
 * block" from "we have no idea" by looking.
 *
 * The tint is deliberately slight. These are the majority of buildings, so
 * anything stronger would repaint the city rather than annotate it.
 */
/**
 * Tinted buildings, keeping the height shading underneath.
 *
 * The level is still driven by height, so a tinted tower still reads as taller
 * than a tinted shed — the colour says what it is, the brightness still says
 * how big. A flat fill would have thrown that away.
 */
function shadeTint(height, tint) {
  // Brightness still tracks height, so a tinted tower reads as taller than a
  // tinted shed; the palette supplies the hue.
  const t = 0.7 + 0.3 * Math.min(height / REFERENCE_HEIGHT_M, 1);
  const base = tint === "blue" ? palette().buildingTintBlue : palette().buildingTintRed;
  return base.map((channel) => Math.round(channel * t));
}

function shadeUnknown(height) {
  const [r, g, b] = shade(height);
  const [mr, mg, mb] = palette().buildingUnknown;
  return [
    Math.min(255, Math.round(r * mr)),
    Math.min(255, Math.round(g * mg)),
    Math.min(255, Math.round(b * mb)),
  ];
}

/** Props shared by the above- and below-grade layers. */
function baseProps(rows, visible) {
  return {
    data: rows,
    visible,
    extruded: true,
    filled: true,
    getPolygon: (d) => d.polygon,
    getElevation: (d) => d.height,
    // A user's tint wins over the police tint: they are looking at the same
    // building and have said something more specific about it.
    getFillColor: (d) => (d.tint ? shadeTint(d.height, d.tint)
      : d.police ? shadeTint(d.height, "red")
      : d.known ? shade(d.height) : shadeUnknown(d.height)),
    material: {
      ambient: 0.35,
      diffuse: 0.6,
      shininess: 8,
      specularColor: palette().buildingSpecular,
    },
    pickable: true,
    parameters: DEPTH_TESTED,
  };
}

/**
 * The city, read from a tile archive.
 *
 * Shading that used to come from passes over the whole array is decided per
 * feature here: a tile that has never been seen before is still coloured
 * correctly, which a precomputed flag on a row could not manage.
 *
 * Tiles register their footprints with the shared index as they load and
 * withdraw them as they unload, so everything that needs to know where
 * buildings are keeps working without holding the whole city in memory.
 */
export function buildingTileLayer({
  url, visible = true, isKnown, tintAt, onChange, shadingStamp = 0,
} = {}) {
  // Everything the fill colour depends on beyond the feature itself: the
  // theme, and the isKnown/tintAt closures (stamped by the caller when they
  // are rebuilt). This string must reach the SUBLAYER's getFillColor trigger:
  // a tile's content array keeps its identity across re-renders, so without
  // it deck reuses the colour buffer tessellated under the old theme.
  const fillStamp = `${themeName()}:${shadingStamp}`;
  return pmTileLayer({
    id: "buildings",
    url,
    visible,
    // ── Why these three lines are the difference between a city and a plain ──
    //
    // The city rendered as flat ground with the geometry all present: tiles
    // loaded, features parsed, sublayers built, colours right, and nothing on
    // screen. Measured live, the layer was holding 62 sublayers carrying
    // 119,233 polygons across zooms 13 to 16 at the same time, of which 10 had
    // reached the GPU. SolidPolygonLayer tessellates on the main thread, so
    // that backlog never cleared — and the tiles kept churning as the camera
    // moved, so it never could.
    //
    // minZoom 14 removes the worst of it. A single downtown z13 tile carries
    // 15,000 footprints and they are a grey smear at that scale; buildings are
    // only worth drawing once they read as buildings.
    minZoom: 14,
    maxZoom: 16,
    // Parents stop drawing once their children are in. The default keeps them
    // underneath, which is what put four zoom levels of the same blocks on
    // screen at once and quadrupled the tessellation bill for no visible gain.
    refinementStrategy: "no-overlap",
    // Bounded so panning cannot accumulate an unbounded backlog of tiles that
    // all still want tessellating.
    maxCacheSize: 48,
    updateTriggers: { renderSubLayers: fillStamp },

    onTileLoad: (tile) => {
      const data = Array.isArray(tile?.content) ? tile.content : null;
      if (!data?.length) return;
      footprints.add(String(tile.id), data.map((f) => ({
        polygon: f.geometry.coordinates,
        height: Number(f.properties?.h) || 3,
      })));
      onChange?.();
    },
    onTileUnload: (tile) => {
      footprints.remove(String(tile.id));
    },

    renderSubLayers: (props) => {
      const data = tileFeatures(props);
      if (!data?.length) return null;

      return new SolidPolygonLayer(props, {
        id: `${props.id}-fill`,
        data,
        extruded: true,
        filled: true,
        getPolygon: (f) => f.geometry.coordinates,
        // Tippecanoe preserves properties verbatim, so `h` is still metres.
        getElevation: (f) => Number(f.properties?.h) || 3,
        getFillColor: (f) => {
          const height = Number(f.properties?.h) || 3;
          const tint = tintAt?.(f);
          if (tint) return shadeTint(height, tint);
          return isKnown?.(f) ? shade(height) : shadeUnknown(height);
        },
        material: {
          ambient: 0.35, diffuse: 0.6, shininess: 8,
          specularColor: palette().buildingSpecular,
        },
        // The parent's trigger only re-runs THIS function; the sublayer keeps
        // its tessellated colour buffer unless its own accessor is triggered,
        // because the tile's data array never changes identity.
        updateTriggers: { getFillColor: fillStamp },
        pickable: true,
        parameters: DEPTH_TESTED,
      });
    },
  });
}

export function buildingLayer(rows, { visible = true } = {}) {
  return new SolidPolygonLayer({
    id: "buildings",
    ...baseProps(rows, visible),
    updateTriggers: { getFillColor: `${rows.knownStamp ?? 0}:${themeName()}` },
  });
}

/** Legend rows for whichever conduit kinds are actually present. */

/**
 * Height of the building under a point, or 0 on open ground.
 *
 * Kept as a function so callers read the same way whether the footprints came
 * from a tile or from the whole file; the index behind it is shared.
 */
export function makeGroundHeight() {
  return (point) => footprints.heightAt(point);
}

/**
 * Centre of a tile feature's footprint.
 *
 * Outer ring only — an interior ring is a courtyard, and averaging it in pulls
 * the centre toward a hole in the building.
 */
function featureCentre(feature) {
  const ring = outerRing(feature?.geometry?.coordinates);
  if (!Array.isArray(ring) || !ring.length) return null;
  let x = 0;
  let y = 0;
  for (const point of ring) { x += point[0]; y += point[1]; }
  return [x / ring.length, y / ring.length];
}

/**
 * "Is anything known about this building?", per feature.
 *
 * Was a pass over the whole array that stamped a flag on each row. Tiles
 * arrive after any such pass would have run, so the question is asked at draw
 * time instead — and the answer is no worse for it, since it was always a
 * lookup rather than a computation.
 */
export function makeIsKnown(places, edits) {
  return (feature) => {
    const centre = featureCentre(feature);
    if (!centre) return false;
    const place = places?.nearest(centre);
    return Boolean(place?.name || place?.use || place?.amenity
      || place?.operator || edits?.find(centre));
  };
}

/**
 * The colour a building should be drawn in, if it is not the default.
 *
 * A user's own tint wins over the automatic police one: they are looking at
 * the same building and have said something more specific about it.
 */
export function makeTintAt(edits, stations = []) {
  const points = stations
    .filter((s) => Number.isFinite(s.lng) && Number.isFinite(s.lat))
    .map((s) => [s.lng, s.lat]);

  return (feature) => {
    const centre = featureCentre(feature);
    if (!centre) return null;

    const edited = edits?.find(centre)?.fields?.tint;
    if (edited) return edited;

    const ring = outerRing(feature?.geometry?.coordinates);
    if (!ring) return null;
    for (const [lng, lat] of points) {
      // Containment first; a station mapped as a point on the kerb falls back
      // to proximity, which is why the centroid distance is checked too.
      if (inRing(lng, lat, ring)) return "red";
      const dx = (centre[0] - lng) * 85600;
      const dy = (centre[1] - lat) * 110540;
      if (Math.hypot(dx, dy) < 45) return "red";
    }
    return null;
  };
}
